import type {FormPageFragment, FormPageRuleInput, PageRuleFragment} from 'nutshell-graphql-types';
import {FormFieldType, PageRuleActionType} from 'nutshell-graphql-types';

import type {FieldWithArrayKey} from '../sidebar/blocks-and-design/content-blocks/types';
import type {FormPage} from '../types';

import type {NavOption} from './types';

export const NAV_DESTINATION_NEXT_PAGE = 'nav:next-page';
export const NAV_DESTINATION_SUBMIT = 'nav:submit';

const NAV_DESTINATION_SPECIFIC_PAGE_PREFIX = 'nav:page:';

export function makeSpecificPageNavOptionId(targetFormPageId: string): string {
    return `${NAV_DESTINATION_SPECIFIC_PAGE_PREFIX}${targetFormPageId}`;
}

function parseTargetPageIdFromOption(optionId: string): string | null {
    if (!optionId.startsWith(NAV_DESTINATION_SPECIFIC_PAGE_PREFIX)) {
        return null;
    }
    const id = optionId.slice(NAV_DESTINATION_SPECIFIC_PAGE_PREFIX.length);
    return id || null;
}

function optimisticContinueNavOptionId(isLastPage: boolean): string {
    return isLastPage ? NAV_DESTINATION_SUBMIT : NAV_DESTINATION_NEXT_PAGE;
}

/**
 * Maps the stored default rule to a dropdown option id. On the last page, the
 * backend still stores `CONTINUE` by default; at runtime the form treats that
 * as submit. We show "Submit form" for `CONTINUE` on the last page only, there
 * is no "Next page" option there.
 *
 * When `rule` is missing, we show the same as the backend default for a new
 * page: `CONTINUE`.
 */
export function defaultRuleToNavOptionId(
    rule: PageRuleFragment | null | undefined,
    isLastPage: boolean
): string | null {
    if (!rule) {
        return optimisticContinueNavOptionId(isLastPage);
    }
    switch (rule.actionType) {
        case PageRuleActionType.CONTINUE:
            return isLastPage ? NAV_DESTINATION_SUBMIT : NAV_DESTINATION_NEXT_PAGE;
        case PageRuleActionType.SUBMIT:
            return NAV_DESTINATION_SUBMIT;
        case PageRuleActionType.PAGE:
            return rule.targetPage?.id ? makeSpecificPageNavOptionId(rule.targetPage.id) : null;
        default:
            return null;
    }
}

/**
 * Builds dropdown options, excluding the source page as a target.
 */
export function buildNavDestinationOptions(args: {
    sourceFormPageId: string | null | undefined;
    isLastPage: boolean;
    pages: NavOption[];
}): {id: string; name: string}[] {
    const {sourceFormPageId, isLastPage, pages} = args;

    const out: {id: string; name: string}[] = [];

    if (!isLastPage) {
        out.push({id: NAV_DESTINATION_NEXT_PAGE, name: 'Next page'});
    }

    out.push({id: NAV_DESTINATION_SUBMIT, name: 'Submit form'});

    for (const page of pages) {
        if (page.id === sourceFormPageId) {
            continue;
        }
        out.push({
            id: makeSpecificPageNavOptionId(page.id),
            name: page.label,
        });
    }

    return out;
}

/**
 * Converts a selected dropdown value into the `defaultRule` payload for `formPageRulesUpdate`.
 */
export function navOptionToFormPageRuleInput(
    optionId: string,
    existingRuleId: string | null | undefined
): FormPageRuleInput {
    const id = existingRuleId ?? null;

    if (optionId === NAV_DESTINATION_NEXT_PAGE) {
        return {id, actionType: PageRuleActionType.CONTINUE, targetPageId: null};
    }

    if (optionId === NAV_DESTINATION_SUBMIT) {
        return {id, actionType: PageRuleActionType.SUBMIT, targetPageId: null};
    }

    const targetPageId = parseTargetPageIdFromOption(optionId);

    if (targetPageId !== null) {
        return {id, actionType: PageRuleActionType.PAGE, targetPageId};
    }

    return {id, actionType: PageRuleActionType.CONTINUE, targetPageId: null};
}

/**
 * Match this canvas row to editableVersion.pages by stable page API id when available.
 * Array index alone can be wrong while RHF has already removed/reordered pages and Apollo
 * has not caught up from the debounced formUpdate.
 */
export function getGraphqlPageForCanvasRow(
    graphqlPages: FormPageFragment[] | null | undefined,
    rhfPageIndex: number,
    rhfPage: Pick<FormPage, 'id'>
): FormPageFragment | undefined {
    if (!graphqlPages?.length) {
        return undefined;
    }

    const hintId = rhfPage.id ?? graphqlPages[rhfPageIndex]?.id ?? null;

    if (hintId) {
        return graphqlPages.find((p) => p.id === hintId);
    }

    return graphqlPages[rhfPageIndex];
}

export function isPageContentEmpty(fields: FieldWithArrayKey[]): boolean {
    return fields.length === 0 || fields.every((f) => f.type === FormFieldType.SUBMIT_BUTTON);
}
